import { create } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    keepPreviousData,
    queryOptions,
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import type { GitpodAPI } from "@/api";
import { useCursorPagination } from "@/hooks/use-cursor-pagination";
import { useEnvironmentClassMap, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useEventInterest } from "@/hooks/use-event-interest";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { groupsQueryKey, useGetGroup, useListRoleAssignments } from "@/queries/group-queries";
import { createInfiniteQueryUpdater } from "@/queries/infinite-query-cache";
import { useOrganization } from "@/queries/organization-queries";
import { autoPaginate, DEFAULT_PAGE_SIZE } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { MessageInit } from "@/utils/protobuf";
import { CountResponseRelation } from "gitpod-next-api/gitpod/v1/count_pb";
import {
    type EnvironmentInitializer_Spec,
    GitInitializer_CloneTargetMode,
    type EnvironmentInitializer,
    type GitInitializer,
    EnvironmentInitializer_SpecSchema,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { PaginationRequestSchema, SortSchema } from "gitpod-next-api/gitpod/v1/pagination_pb";
import {
    type ListProjectsRequest_Filter,
    type ListProjectEnvironmentClassesRequest,
    type Project,
    type ProjectEnvironmentClass,
    type ProjectPolicy,
    type ProjectPrebuildConfiguration,
    type ProjectRole,
    type RecommendedEditors,
    ListProjectsRequest_FilterSchema,
} from "gitpod-next-api/gitpod/v1/project_pb";
import { type RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";

export const projectsQueryKey = {
    listByIds: (projectIds: string[]) => keyWithPrincipal(["projects", "list", "byIds", { projectIds }]),
    list: (filter?: MessageInit<ListProjectsRequest_Filter>) =>
        keyWithPrincipal(["projects", "list", "all", { filter }]),
    listPaginated: (filter?: MessageInit<ListProjectsRequest_Filter>, page?: string) =>
        keyWithPrincipal(["projects", "list", "paginated", { filter, page }]),
    listPage: (filter?: MessageInit<ListProjectsRequest_Filter>, page?: number) =>
        keyWithPrincipal(["projects", "list", "page", { filter, page }]),
    cursorPage: (filter?: MessageInit<ListProjectsRequest_Filter>, token?: string) =>
        keyWithPrincipal(["projects", "cursor", { filter, token }]),
    get: (projectId?: string) => keyWithPrincipal(["projects", { projectId }]),
    listPolicies: (projectId?: string) => keyWithPrincipal(["projectsPolicies", "list", { projectId }]),
    listEnvironmentClasses: (projectId?: string) =>
        keyWithPrincipal(["projectEnvironmentClasses", "list", { projectId }]),
    listEnvironmentClassEntries: (projectId?: string) =>
        keyWithPrincipal(["projectEnvironmentClassEntries", "list", { projectId }]),
};

export type PlainProject = Project;

export function toPlainProject(project: Project): PlainProject {
    return project;
}

export type PlainProjectPolicy = ProjectPolicy;

export function toPlainProjectPolicy(policy: ProjectPolicy): PlainProjectPolicy {
    return policy;
}

export type PlainProjectEnvironmentClass = ProjectEnvironmentClass;

export function toPlainProjectEnvironmentClass(envClass: ProjectEnvironmentClass): PlainProjectEnvironmentClass {
    return envClass;
}

export const handleProjectEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    console.debug(`[EventHandler] Project event:`, {
        operation: evt.operation,
        resourceId: evt.resourceId,
    });

    if (evt.operation === ResourceOperation.UPDATE) {
        const project = await refetchProject(api, evt.resourceId);
        setProjectInCache(client, evt.resourceId, project ? toPlainProject(project) : undefined);
        console.debug(`[EventHandler] Project ${evt.resourceId} updated in cache`);
    }
    if (evt.operation === ResourceOperation.CREATE) {
        // Invalidate all list() caches (with different filter parameters)
        // Use prefix-based matching by removing the dynamic filter parameter
        await client.invalidateQueries({ queryKey: projectsQueryKey.list().slice(0, -1) });
        // Invalidate all listPaginated() caches (with different search filters)
        await client.invalidateQueries({ queryKey: projectsQueryKey.listPaginated().slice(0, -1) });
        // Invalidate all listPage() caches (with different page numbers)
        await client.invalidateQueries({ queryKey: projectsQueryKey.listPage().slice(0, -1) });
        // Invalidate all cursorPage() caches (used by ProjectsList)
        await client.invalidateQueries({ queryKey: projectsQueryKey.cursorPage().slice(0, -1) });
        console.debug(`[EventHandler] Project ${evt.resourceId} created, lists invalidated`);
    }
    if (evt.operation === ResourceOperation.DELETE) {
        setProjectInCache(client, evt.resourceId, undefined);
        // Invalidate all list caches so pagination stays consistent
        await client.invalidateQueries({ queryKey: projectsQueryKey.list().slice(0, -1) });
        await client.invalidateQueries({ queryKey: projectsQueryKey.listPaginated().slice(0, -1) });
        await client.invalidateQueries({ queryKey: projectsQueryKey.listPage().slice(0, -1) });
        await client.invalidateQueries({ queryKey: projectsQueryKey.cursorPage().slice(0, -1) });
        console.debug(`[EventHandler] Project ${evt.resourceId} deleted, lists invalidated`);
    }
};

const refetchProject = async (api: GitpodAPI, projectId: string) => {
    try {
        const response = await api.projectService.getProject({ projectId });
        return response.project;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        // this might happen if the project was deleted in the meantime
        if (!isNotFound) {
            console.error("Failed to refetch project", projectId);
        }
    }
};

export function setProjectInCache(client: QueryClient, projectId: string, project?: PlainProject) {
    // Setting the query data to undefined means we skip the update, so we're explicitly setting it to null
    // So we can clear the cache on deletion.
    client.setQueryData(projectsQueryKey.get(projectId), project ? project : null);

    const updateInfiniteQueryData = createInfiniteQueryUpdater(projectId, project, "projects", (p) => p.id);

    // Update the default list() cache (creates if doesn't exist)
    client.setQueryData(
        projectsQueryKey.list({ projectIds: [], runnerIds: [], runnerKinds: [] }),
        updateInfiniteQueryData,
    );

    // Update all other list() caches with different filter parameters
    // Use prefix-based matching by removing the dynamic filter parameter
    client.setQueriesData({ queryKey: projectsQueryKey.list().slice(0, -1) }, updateInfiniteQueryData);

    // Update all listPaginated() caches (with different search filters)
    client.setQueriesData({ queryKey: projectsQueryKey.listPaginated().slice(0, -1) }, updateInfiniteQueryData);

    // Update all listPage() caches (each page is a separate cache entry now)
    client.setQueriesData(
        { queryKey: projectsQueryKey.listPage().slice(0, -1) },
        (currentData?: ProjectListPageData) => {
            if (!currentData) return currentData;

            // Update or delete the project in this page
            const updatedProjects = currentData.projects
                .map((p) => (p.id === projectId ? project : p))
                .filter((w): w is PlainProject => !!w);

            return {
                ...currentData,
                projects: updatedProjects,
            };
        },
    );

    // Update all cursorPage() caches (used by ProjectsList)
    client.setQueriesData(
        { queryKey: projectsQueryKey.cursorPage().slice(0, -1) },
        (currentData?: ProjectsCursorPageData) => {
            if (!currentData) return currentData;

            const updatedProjects = currentData.projects
                .map((p) => (p.id === projectId ? project : p))
                .filter((w): w is PlainProject => !!w);

            return {
                ...currentData,
                projects: updatedProjects,
            };
        },
    );
}

function setProjectPolicyInCache(client: QueryClient, projectId: string, groupId: string, policy?: ProjectPolicy) {
    client.setQueryData(
        projectsQueryKey.listPolicies(projectId),
        (currentData?: { policies: PlainProjectPolicy[] }) => {
            if (!currentData) {
                return currentData;
            }
            return {
                ...currentData,
                projectId,
                policies: currentData.policies
                    .map((p) => {
                        if (p.groupId !== groupId) {
                            return p;
                        }
                        return policy;
                    })
                    .filter((w) => !!w),
            };
        },
    );
}

export const useCreateProject = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            name,
            contextURL,
            devcontainerFilePath,
            automationsFilePath,
            gitConfig,
        }: {
            name: string;
            contextURL: string;
            devcontainerFilePath?: string;
            automationsFilePath?: string;
            gitConfig?: {
                remoteUrl: string;
                ref: string;
                upstreamRemoteUrl?: string;
                checkoutLocation?: string;
            };
        }) => {
            // Create initializer specs based on whether we have Git config
            let initializers: EnvironmentInitializer_Spec[] = [];
            if (gitConfig) {
                initializers = [
                    create(EnvironmentInitializer_SpecSchema, {
                        spec: {
                            case: "git",
                            value: {
                                remoteUri: gitConfig.remoteUrl,
                                targetMode: GitInitializer_CloneTargetMode.REMOTE_BRANCH,
                                cloneTarget: gitConfig.ref,
                                upstreamRemoteUri: gitConfig.upstreamRemoteUrl || "",
                                checkoutLocation: gitConfig.checkoutLocation || "",
                            } as GitInitializer,
                        },
                    }),
                ];
            } else {
                initializers = [
                    create(EnvironmentInitializer_SpecSchema, {
                        spec: { case: "contextUrl", value: { url: contextURL } },
                    }),
                ];
            }

            const { project } = await api.projectService.createProject({
                name,
                initializer: {
                    specs: initializers,
                },
                devcontainerFilePath,
                automationsFilePath,
            });

            if (!project) {
                throw new Error("Failed to create project");
            }

            return toPlainProject(project);
        },
        onSuccess: (plainProject) => {
            setProjectInCache(client, plainProject.id, plainProject);
            // Invalidate resource permissions so the creator sees their admin role immediately.
            // Backend events for system-managed groups are not emitted to avoid flooding.
            void client.invalidateQueries({ queryKey: groupsQueryKey.userResourcePermissions() });
        },
    });
};

export const useCreateProjectFromEnvironment = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectName, environmentId }: { projectName: string; environmentId: string }) => {
            const { project } = await api.projectService.createProjectFromEnvironment({
                name: projectName,
                environmentId,
            });

            if (!project) {
                throw new Error("Failed to create project");
            }

            return toPlainProject(project);
        },
        onSuccess: (plainProject) => {
            setProjectInCache(client, plainProject.id, plainProject);
            // Invalidate resource permissions so the creator sees their admin role immediately.
            // Backend events for system-managed groups are not emitted to avoid flooding.
            void client.invalidateQueries({ queryKey: groupsQueryKey.userResourcePermissions() });
        },
    });
};

export const useUpdateProject = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            projectId,
            name,
            initializer,
            devcontainerFilePath,
            automationsFilePath,
            prebuildConfiguration,
            recommendedEditors,
        }: {
            projectId: string;
            name?: string;
            initializer?: MessageInit<EnvironmentInitializer>;
            devcontainerFilePath?: string;
            automationsFilePath?: string;
            prebuildConfiguration?: MessageInit<ProjectPrebuildConfiguration>;
            recommendedEditors?: MessageInit<RecommendedEditors>;
        }) => {
            const { project } = await api.projectService.updateProject({
                projectId,
                name,
                initializer,
                devcontainerFilePath,
                automationsFilePath,
                prebuildConfiguration,
                recommendedEditors,
            });

            return toPlainProject(project!);
        },
        onSuccess: (plainProject) => {
            // We're invalidating queries based on events, so no need to invalidate here.
            setProjectInCache(queryClient, plainProject.id, plainProject);
        },
    });
};

export const useListProjectsByIds = (projectIds: string[], { enabled = true }: { enabled?: boolean } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    // max number of project IDs we can filter for in a single call is 25 (see project.proto)
    const batchSize = 25;

    const batches = useMemo(() => {
        const result: string[][] = [];
        for (let i = 0; i < projectIds.length; i += batchSize) {
            result.push(projectIds.slice(i, i + batchSize));
        }
        return result;
    }, [projectIds]);

    return useQueries({
        queries: batches.map((batch) => ({
            queryKey: projectsQueryKey.listByIds(batch),
            queryFn: async () => {
                const response = await api.projectService.listProjects({
                    filter: create(ListProjectsRequest_FilterSchema, { projectIds: batch }),
                });
                return response.projects.map(toPlainProject);
            },
            enabled: enabled && Boolean(user),
            staleTime: 5 * 60 * 1000,
            retry: defaultRetry,
        })),
        combine: (results) => ({
            projects: results.flatMap((r) => r.data ?? []),
            isPending: results.some((r) => r.isPending),
            isError: results.some((r) => r.isError),
            errors: results.filter((r) => r.error).map((r) => r.error),
            isPartialSuccess: results.some((r) => r.isSuccess) && results.some((r) => r.isError),
        }),
    });
};

/**
 * Fetches projects by IDs in batch and seeds individual project caches.
 * Use this to avoid N+1 useProject calls when you know the project IDs upfront.
 */
export const usePrefetchProjectsByIds = (projectIds: string[]) => {
    const client = useQueryClient();
    const result = useListProjectsByIds(projectIds, { enabled: projectIds.length > 0 });

    // Seed individual project caches so downstream useProject() calls hit cache.
    // Uses prefetchQuery with the real getProjectQuery options so that the query
    // respects staleTime and doesn't bypass the queryFn for queries that already
    // have observers (e.g. useProject on a project settings page).
    const seededIdsRef = useRef("");
    const api = useGitpodAPI();
    useEffect(() => {
        if (result.projects.length === 0) return;
        const ids = result.projects
            .map((p) => p.id)
            .sort()
            .join(",");
        if (ids === seededIdsRef.current) return;
        seededIdsRef.current = ids;
        for (const project of result.projects) {
            const queryKey = projectsQueryKey.get(project.id);
            // Only seed if no query exists yet — don't interfere with active useProject() queries
            const existing = client.getQueryCache().find({ queryKey });
            if (existing) continue;
            client.setQueryData(queryKey, project);
        }
    }, [api, client, result.projects]);

    return result;
};

export type UseListProjectsPaginatedParams = {
    page?: string;
    enabled?: boolean;
} & MessageInit<ListProjectsRequest_Filter>;

export const useListProjectsPaginated = ({
    page,
    search,
    runnerIds,
    runnerKinds,
    enabled = true,
}: UseListProjectsPaginatedParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    useEventInterest({ resourceType: ResourceType.PROJECT });

    return useInfiniteQuery({
        queryKey: projectsQueryKey.listPaginated(
            {
                search,
                runnerIds,
                runnerKinds,
            },
            page,
        ),
        queryFn: async ({ pageParam }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.projectService.listProjects({
                pagination: create(PaginationRequestSchema, { token: pageParam }),
                filter: create(ListProjectsRequest_FilterSchema, {
                    search,
                    runnerIds,
                    runnerKinds,
                }),
                sort: create(SortSchema, { field: "popularity" }),
            });

            return {
                projects: response.projects.map(toPlainProject),
                pagination: response.pagination,
            };
        },
        initialPageParam: page,
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination?.nextToken) {
                return undefined;
            }
            return lastPage.pagination.nextToken || undefined;
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
    });
};

type ProjectListPageData = {
    projects: PlainProject[];
    totalCount?: number;
    totalPages?: number;
    pageSize: number;
    nextToken?: string;
};

export type UseProjectListPageParams = {
    page?: number;
    search?: string;
    runnerIds?: string[];
    runnerKinds?: RunnerKind[];
    enabled?: boolean;
};

export const useGetProjectListPageOptions = ({
    page = 0,
    search,
    runnerIds,
    runnerKinds,
    enabled = true,
}: UseProjectListPageParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = projectsQueryKey.listPage({ search, runnerIds, runnerKinds }, page);
    return queryOptions({
        queryKey,
        queryFn: async (): Promise<ProjectListPageData> => {
            const token = page === 0 ? "" : String(page);
            const response = await api.projectService.listProjects({
                pagination: create(PaginationRequestSchema, { token }),
                filter: create(ListProjectsRequest_FilterSchema, { search, runnerIds, runnerKinds }),
                sort: create(SortSchema, { field: "popularity" }),
            });

            const projects = response.projects.map(toPlainProject);
            const pageSize = 25;

            // Extract metadata from pagination token
            let totalPages: number | undefined;
            let totalCount: number | undefined;

            if (response.pagination?.nextToken) {
                const parts = response.pagination.nextToken.split("/");
                if (parts.length === 3) {
                    totalPages = parseInt(parts[1], 10);
                    totalCount = parseInt(parts[2], 10);
                }
            } else if (page === 0) {
                // No nextToken on page 0 means all data fits on one page
                totalPages = 1;
                totalCount = projects.length;
            }

            return {
                projects,
                totalCount,
                totalPages,
                pageSize,
                nextToken: response.pagination?.nextToken,
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
    });
};
/**
 * Simple hook that fetches a single page of projects.
 * Each page has its own cache entry.
 */
export const useProjectListPage = ({
    page = 0,
    search,
    runnerIds,
    runnerKinds,
    enabled = true,
}: UseProjectListPageParams = {}) => {
    const queryOptions = useGetProjectListPageOptions({ page, search, runnerIds, runnerKinds, enabled });
    return useQuery(queryOptions);
};

/**
 * Wrapper hook that fetches both the requested page and page 0 (for metadata).
 * React Query handles deduplication if page === 0.
 */
export const usePaginatedProjects = ({
    page = 0,
    search,
    runnerIds,
    runnerKinds,
    enabled = true,
}: UseProjectListPageParams = {}) => {
    const queryClient = useQueryClient();

    // Fetch the requested page
    const pageQuery = useProjectListPage({ page, search, runnerIds, runnerKinds, enabled });

    // Fetch page 0 for metadata (React Query deduplicates if page === 0)
    const page0Query = useProjectListPage({ page: 0, search, runnerIds, runnerKinds, enabled });

    // Use metadata from page 0, fall back to current page
    const metadata = {
        totalCount: page0Query.data?.totalCount ?? pageQuery.data?.totalCount,
        totalPages: page0Query.data?.totalPages ?? pageQuery.data?.totalPages ?? 0,
        pageSize: page0Query.data?.pageSize ?? 25,
        nextToken: pageQuery.data?.nextToken,
    };

    const queryOptions = useGetProjectListPageOptions({ page: page + 1, search, runnerIds, runnerKinds, enabled });

    // Prefetch the next page
    useEffect(() => {
        void queryClient.prefetchQuery(queryOptions).catch(console.error);

        // ignoring queryOptions, using its dependencies instead
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, page, search, runnerIds, runnerKinds, queryClient]);

    const data = useMemo(() => {
        return pageQuery.data?.projects ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        data,
        metadata,
        ...metadata,
    };
};

type UseProjectsCursorPageParams = {
    search?: string;
    runnerIds?: string[];
    runnerKinds?: RunnerKind[];
    enabled?: boolean;
};

type ProjectsCursorPageData = {
    projects: PlainProject[];
    nextToken: string;
    totalCount?: number;
    totalCountIsEstimate?: boolean;
};

export const useProjectsCursorPageOptions = ({
    filter,
    token,
    enabled = true,
}: {
    filter: ListProjectsRequest_Filter;
    token?: string;
    enabled?: boolean;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: projectsQueryKey.cursorPage(filter, token),
        queryFn: async (): Promise<ProjectsCursorPageData> => {
            const isFirstPage = !token;
            const response = await api.projectService.listProjects({
                pagination: create(PaginationRequestSchema, {
                    pageSize: DEFAULT_PAGE_SIZE,
                    token,
                }),
                filter,
                sort: create(SortSchema, { field: "popularity" }),
                count: isFirstPage ? { include: true } : undefined,
            });

            const projects = response.projects.map(toPlainProject);

            return {
                projects,
                nextToken: response.pagination?.nextToken ?? "",
                totalCount: response.count?.value,
                totalCountIsEstimate: response.count?.relation === CountResponseRelation.GTE,
            };
        },
        enabled: enabled && !!user,
        staleTime: 5 * 60 * 1000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Fetches a single page of projects using an opaque cursor token.
 * Manages cursor state internally and resets when filters change.
 */
export const useProjectsCursorPage = ({
    search,
    runnerIds,
    runnerKinds,
    enabled = true,
}: UseProjectsCursorPageParams) => {
    const cursor = useCursorPagination();

    // Reset cursor when filters change
    const filterKey = JSON.stringify({ search, runnerIds, runnerKinds });
    const prevFilterKeyRef = useRef(filterKey);
    useEffect(() => {
        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            cursor.reset();
        }
    }, [filterKey, cursor]);

    const filter = useMemo(
        () => create(ListProjectsRequest_FilterSchema, { search, runnerIds, runnerKinds }),
        [search, runnerIds, runnerKinds],
    );

    const query = useQuery(useProjectsCursorPageOptions({ filter, token: cursor.currentToken, enabled }));

    return { ...query, cursor };
};

/**
 * The query for getting a single project by ID.
 * It's split from useProject to allow for re-use (like we do in the archived sidebar)
 */
export const getProjectQuery = (api: GitpodAPI, projectId?: string) => {
    return queryOptions({
        queryKey: projectsQueryKey.get(projectId),
        queryFn: async (): Promise<PlainProject> => {
            if (!projectId) {
                throw new Error("No project ID provided");
            }

            const { project } = await api.projectService.getProject({ projectId });
            if (!project) {
                throw new Error("Project not found");
            }

            return toPlainProject(project);
        },
        enabled: !!projectId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useProject = (projectId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainProject, Error>(getProjectQuery(api, projectId));
};

export const useDeleteProject = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({ projectId }: { projectId: string }) => {
            await api.projectService.deleteProject({
                projectId,
            });
        },
        onSuccess: (_, { projectId }) => {
            // We're invalidating queries based on events, so no need to invalidate here.
            setProjectInCache(queryClient, projectId, undefined);
        },
    });
};

export const useCreateProjectPolicy = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ projectId, groupId, role }: { projectId: string; groupId: string; role: ProjectRole }) => {
            const { policy } = await api.projectService.createProjectPolicy({
                projectId,
                groupId,
                role,
            });

            return toPlainProjectPolicy(policy!);
        },
        onSettled: async (_, __, { projectId }) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: projectsQueryKey.listPolicies(projectId) }),
                // Invalidate role assignments used by useProjectState/useListProjectStates
                client.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(ResourceType.PROJECT) }),
            ]);
        },
    });
};

export const useDeleteProjectPolicy = () => {
    const queryClient = useQueryClient();
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({ projectId, groupId }: { projectId: string; groupId: string }) => {
            await api.projectService.deleteProjectPolicy({
                projectId,
                groupId,
            });

            setProjectPolicyInCache(queryClient, projectId, groupId, undefined);
        },
        onSettled: async (_, __, { projectId }) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: projectsQueryKey.listPolicies(projectId) }),
                // Invalidate role assignments used by useProjectState/useListProjectStates
                queryClient.invalidateQueries({ queryKey: groupsQueryKey.roleAssignments(ResourceType.PROJECT) }),
            ]);
        },
    });
};

export type ProjectState = {
    projectId: string;
    orgId: string;
    orgMembersGroupId: string;
    shared: boolean | undefined;
    environmentClasses: PlainProjectEnvironmentClass[] | undefined;
};

export const useProjectState = (
    projectId?: string,
): { isLoading: boolean; error: Error | undefined; data: ProjectState | undefined } => {
    const { data: organization, isPending: isLoadingOrganization, error: organizationError } = useOrganization();
    const {
        data: orgMembersGroup,
        isPending: isLoadingGroup,
        error: groupError,
    } = useGetGroup({ name: "org-members" });
    const { data: environmentClasses, error: environmentClassesError } = useListProjectEnvironmentClasses(projectId);

    const { data: orgMembersAssignments, error: assignmentsError } = useListRoleAssignments({
        resourceType: ResourceType.PROJECT,
        resourceId: projectId,
        groupId: orgMembersGroup?.id,
        enabled: !!orgMembersGroup && !!projectId,
    });

    const sharingValue = useMemo(() => {
        if (!orgMembersAssignments || !orgMembersGroup) {
            return;
        }

        // Query already filters by groupId, so any result means shared
        return orgMembersAssignments.assignments.length > 0;
    }, [orgMembersAssignments, orgMembersGroup]);

    const isLoading = isLoadingOrganization || isLoadingGroup;
    const error = organizationError || groupError || assignmentsError || environmentClassesError;

    return {
        isLoading,
        error: error || undefined,
        data:
            !!projectId && organization && orgMembersGroup
                ? {
                      orgId: organization.id,
                      projectId: projectId,
                      orgMembersGroupId: orgMembersGroup.id,
                      shared: typeof sharingValue !== "undefined" ? sharingValue : undefined,
                      environmentClasses: environmentClasses?.environmentClasses,
                  }
                : undefined,
    };
};

export const useListProjectStates = (
    projectIds?: string[],
): { isLoading: boolean; data: ProjectState[] | undefined } => {
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    const { data: orgMembersGroup, isPending: isLoadingGroup } = useGetGroup({ name: "org-members" });
    const allProjectEnvironmentClasses = useListProjectsEnvironmentClasses(projectIds);

    // Single batched call replaces N per-project ListProjectPolicies calls.
    // Filtering by groupId makes the backend return system-managed group
    // assignments on all tiers, matching the old ListProjectPolicies behavior.
    const { data: orgMembersAssignments } = useListRoleAssignments({
        resourceType: ResourceType.PROJECT,
        groupId: orgMembersGroup?.id,
        enabled: !!orgMembersGroup,
    });

    const sharingState: Record<string, boolean> = useMemo(() => {
        if (!orgMembersGroup || !orgMembersAssignments || !projectIds) {
            return {} as Record<string, boolean>;
        }

        const sharedProjectIds = new Set(orgMembersAssignments.assignments.map((a) => a.resourceId));

        return projectIds.reduce(
            (acc, projectId) => {
                acc[projectId] = sharedProjectIds.has(projectId);
                return acc;
            },
            {} as Record<string, boolean>,
        );
    }, [orgMembersGroup, orgMembersAssignments, projectIds]);

    const projectEnvironmentClassesState: Record<string, PlainProjectEnvironmentClass[]> = useMemo(() => {
        return allProjectEnvironmentClasses.reduce(
            (acc, curr) => {
                if (curr.data) {
                    acc[curr.data.projectId] = curr.data.environmentClasses;
                }
                return acc;
            },
            {} as Record<string, PlainProjectEnvironmentClass[]>,
        );
    }, [allProjectEnvironmentClasses]);

    const isLoading = isLoadingOrganization || isLoadingGroup;

    return {
        isLoading,
        data:
            organization && orgMembersGroup && projectIds
                ? projectIds.map((projectId) => {
                      return {
                          orgId: organization.id,
                          projectId: projectId,
                          orgMembersGroupId: orgMembersGroup.id,
                          shared: typeof sharingState[projectId] !== "undefined" ? sharingState[projectId] : undefined,
                          environmentClasses:
                              typeof projectEnvironmentClassesState[projectId] !== "undefined"
                                  ? projectEnvironmentClassesState[projectId]
                                  : undefined,
                      };
                  })
                : undefined,
    };
};

export const useListProjectEnvironmentClassesOptions = (projectId?: string, enabled = true) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: projectsQueryKey.listEnvironmentClasses(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const environmentClasses = await autoPaginate(
                (params) =>
                    api.projectService.listProjectEnvironmentClasses(params as ListProjectEnvironmentClassesRequest),
                { projectId, pagination: create(PaginationRequestSchema, { pageSize: 30 }) }, // For now max total environment classes per project is 30
                (response) => response.projectEnvironmentClasses,
            );

            return {
                projectId,
                environmentClasses: environmentClasses.map(toPlainProjectEnvironmentClass),
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: enabled && !!user && !!projectId,
    });
};

export const useListProjectEnvironmentClasses = (projectId?: string, enabled = true) => {
    return useQuery(useListProjectEnvironmentClassesOptions(projectId, enabled));
};

export const useListProjectEnvironmentClassEntriesOptions = ({
    projectId,
    enabled = true,
}: {
    projectId?: string;
    enabled?: boolean;
}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { environmentClasses, isLoading: isLoadingEnvironmentClasses } = useEnvironmentClassMap();

    return queryOptions({
        queryKey: projectsQueryKey.listEnvironmentClassEntries(projectId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentClasses) {
                throw new Error("failed to query for environment classes");
            }

            const projectEnvClasses = await autoPaginate(
                (params) =>
                    api.projectService.listProjectEnvironmentClasses(params as ListProjectEnvironmentClassesRequest),
                { projectId, pagination: create(PaginationRequestSchema, { pageSize: 30 }) }, // For now max total environment classes per project is 30
                (response) => response.projectEnvironmentClasses,
            );

            const entries: EnvironmentClassEntry[] = [];
            for (const clazz of projectEnvClasses) {
                if (clazz.environmentClass.case === "environmentClassId") {
                    const entry = environmentClasses[clazz.environmentClass.value];
                    if (entry) {
                        entries.push(entry);
                    }
                }
            }

            return {
                projectId,
                environmentClasses: entries,
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!projectId && !isLoadingEnvironmentClasses && enabled,
    });
};

/**
 * Like {@link useListProjectEnvironmentClasses}, but returns a list of {@link EnvironmentClassEntry} for a given project
 * @param projectId The ID of the project that this query is querying for
 */
export const useListProjectEnvironmentClassEntries = ({
    projectId,
    enabled = true,
}: {
    projectId?: string;
    enabled?: boolean;
}) => {
    return useQuery(useListProjectEnvironmentClassEntriesOptions({ projectId, enabled }));
};

export const useListProjectsEnvironmentClasses = (projectIds?: string[]) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQueries({
        queries: projectIds
            ? projectIds.map((projectId) => {
                  return {
                      queryKey: projectsQueryKey.listEnvironmentClasses(projectId),
                      queryFn: async () => {
                          if (!user) {
                              throw new Error("User not authenticated");
                          }

                          const environmentClasses = await autoPaginate(
                              (params) =>
                                  api.projectService.listProjectEnvironmentClasses(
                                      params as ListProjectEnvironmentClassesRequest,
                                  ),
                              { projectId, pagination: create(PaginationRequestSchema, { pageSize: 30 }) }, // For now max total environment classes per project is 30
                              (response) => response.projectEnvironmentClasses,
                          );

                          return {
                              projectId,
                              environmentClasses: environmentClasses.map(toPlainProjectEnvironmentClass),
                          };
                      },
                      staleTime: 60_000,
                      throwOnError: defaultThrowOnError,
                      retry: defaultRetry,
                  };
              })
            : [],
    });
};

export const useUpdateProjectEnvironmentClasses = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            projectId,
            projectEnvironmentClasses,
        }: {
            projectId: string;
            projectEnvironmentClasses: PlainProjectEnvironmentClass[];
        }) => {
            await api.projectService.updateProjectEnvironmentClasses({
                projectId,
                projectEnvironmentClasses,
            });
        },
        onSuccess: async (_, { projectId }) => {
            await queryClient.invalidateQueries({ queryKey: projectsQueryKey.listEnvironmentClasses(projectId) });
        },
    });
};
